import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { isPlatform } from "@ionic/react";

/**
 * StripeReturn Component
 *
 * This component handles Stripe Checkout redirects in Capacitor.
 * When Stripe redirects here after payment, it automatically closes the Capacitor Browser
 * and dispatches an event to notify the app about the payment status.
 *
 * Flow:
 * 1. Stripe redirects to: http://192.168.0.178:3000/stripe-return?payment=success&productId=xxx
 * 2. This component detects it's in Capacitor Browser
 * 3. Reads payment status and productId from URL
 * 4. Redirects to native scheme to close browser and notify app
 * 5. App.tsx handles the callback and dispatches event
 */
const StripeReturn: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paymentStatus = urlParams.get("payment");
    const productId = urlParams.get("productId");
    const source = urlParams.get("source");

    // ONLY use native scheme if source=app is EXPLICITLY set
    // This is the ONLY reliable way to know we came from Capacitor Browser
    const isFromCapacitor = source === "app";

    console.log("🔍 StripeReturn: Component detection", {
      source,
      isFromCapacitor,
      paymentStatus,
      productId,
    });

    // If source=app, redirect to native scheme to close Capacitor Browser
    if (isFromCapacitor && (paymentStatus === "success" || paymentStatus === "cancelled")) {
      console.log("📱 StripeReturn: source=app detected, redirecting to native scheme");
      const appId = "com.rotogpt.chat";
      const nativeCallbackUrl = `${appId}://stripe-callback?payment=${paymentStatus}&productId=${productId || ""}`;
      console.log("🔄 StripeReturn: Native URL:", nativeCallbackUrl);

      // IMMEDIATE redirect - try multiple methods
      try {
        window.location.href = nativeCallbackUrl;
      } catch (e) {
        console.error("Error with href, trying replace:", e);
        try {
          window.location.replace(nativeCallbackUrl);
        } catch (e2) {
          console.error("Error with replace, trying assign:", e2);
          window.location.assign(nativeCallbackUrl);
        }
      }
      return;
    }

    // Normal web (no source=app): redirect to home with params
    console.log("🌐 StripeReturn: Web platform detected, redirecting to home");
    // Remove source=app from params if present (shouldn't be, but just in case)
    urlParams.delete("source");
    const cleanSearch = urlParams.toString();
    window.location.replace(`/home${cleanSearch ? "?" + cleanSearch : ""}`);
  }, [location]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#000",
        color: "#fff",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "24px", marginBottom: "16px", color: "white" }}>Processing payment...</div>
        <div style={{ fontSize: "14px", color: "white" }}>Redirecting to app...</div>
      </div>
    </div>
  );
};

export default StripeReturn;
